/**
 * @copyright (C) 2023, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Soonki Min (skmin@rsupport.com)
 *
 * @description
 *
 * @created at Tue Apr 25 2023
 **/

import { useEffect, useState } from "react";
import { useAtomValue } from "jotai";
import { createPortal } from "react-dom";
import { isEmpty, has } from "lodash-es";

import { GroupPopoverType, SinglePopoverType } from "@/frameworks/core/hooks";
import { GROUP_POPOVER_ID, POPOVER_ID, ZIndex } from "@/frameworks/core/commons";
import { usePopover } from "@/frameworks/core/hooks";
import OutsideClickDetector from "./private/OutsideClickDetector";
import PopoverWrapper from "./private/PopoverWrapper";
import popoverListAtom from "./private/atom";

const isGroupPopover = (popoverID: GROUP_POPOVER_ID | POPOVER_ID): popoverID is GROUP_POPOVER_ID =>
	has(GROUP_POPOVER_ID, popoverID);

export default function PopoverProvider() {
	const [anchorPosition, setAnchorPosition] = useState<
		Record<string, { x: number; y: number; width: number; height: number }>
	>({});
	const popoverList = useAtomValue<(SinglePopoverType | GroupPopoverType)[]>(popoverListAtom);

	const { hide, getPopoverState } = usePopover();

	const calcPosition = () => {
		if (popoverList.length === 0) {
			setAnchorPosition({});
			return;
		}
		popoverList.forEach((popover) => {
			const { popoverID, anchorEl } = popover;
			const uniqueID = "uniqueID" in popover ? popover.uniqueID : "";
			if (!getPopoverState(popoverID, uniqueID).isOpen) {
				setAnchorPosition((prev) => {
					delete prev[`${popoverID}${uniqueID}`];
					return { ...prev };
				});
				return;
			}
			if (!anchorEl || !anchorEl.checkVisibility()) return;
			const { top, left, width, height } = anchorEl.getBoundingClientRect();
			setAnchorPosition((prev) => {
				return { ...prev, [`${popoverID}${uniqueID}`]: { x: left, y: top, width, height } };
			});
		});
	};

	const hideOnClickOutside = (ev: MouseEvent, popoverID: POPOVER_ID | GROUP_POPOVER_ID, uniqueID?: string) => {
		const target = ev.target as Element;
		if (isGroupPopover(popoverID)) {
			const groupPopoverList = document.body.querySelectorAll(`#${popoverID}`);
			let stopFlag = false;
			groupPopoverList.forEach((element) => {
				if (element.contains(target)) {
					stopFlag = true;
				}
			});
			const { popover } = getPopoverState(popoverID, uniqueID || "");
			if (!popover || stopFlag) return;
			ev.stopPropagation();
			const { anchorEl, shouldHideOnClickOutside = true } = popover;

			// anchorEl 없을시 분기처리
			if (!anchorEl && target.isEqualNode(document.body)) {
				hide(popoverID, uniqueID || "");
				setAnchorPosition((prev) => {
					delete prev[`${popoverID}${uniqueID}`];
					return { ...prev };
				});
				return;
			}

			if (shouldHideOnClickOutside) {
				if (anchorEl && (anchorEl.contains(target) || anchorEl.isEqualNode(target))) return;
				hide(popoverID, uniqueID || "");
				setAnchorPosition((prev) => {
					delete prev[`${popoverID}${uniqueID}`];
					return { ...prev };
				});
				return;
			}
		} else {
			const { popover } = getPopoverState(popoverID);
			if (!popover) return;
			ev.stopPropagation();
			const { anchorEl, shouldHideOnClickOutside = true } = popover;

			// anchorEl 없을시 분기처리
			if (!anchorEl && target.isEqualNode(document.body)) {
				hide(popoverID);
				setAnchorPosition((prev) => {
					delete prev[`${popoverID}${uniqueID}`];
					return { ...prev };
				});
				return;
			}

			if (shouldHideOnClickOutside) {
				if (anchorEl && (anchorEl.contains(target) || anchorEl.isEqualNode(target))) return;
				hide(popoverID);
				setAnchorPosition((prev) => {
					delete prev[`${popoverID}${uniqueID}`];
					return { ...prev };
				});
				return;
			}
		}
	};

	useEffect(() => {
		calcPosition();
		window.addEventListener("resize", calcPosition);
		return () => {
			window.removeEventListener("resize", calcPosition);
		};
	}, [popoverList.length]);

	if (popoverList.length === 0 || isEmpty(anchorPosition)) return;
	return createPortal(
		<div css={{ position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh", zIndex: ZIndex.POPUP }}>
			{popoverList.map((popover) => {
				const {
					content,
					position,
					popoverID,
					direction,
					overflow,
					zIndex,
					hasMouseClickAction = false,
					shouldHideOnClickOutside = true
				} = popover;
				const uniqueID = "uniqueID" in popover ? popover.uniqueID : "";

				return (
					<OutsideClickDetector
						key={`${popoverID}${uniqueID}`}
						active={shouldHideOnClickOutside}
						id={popoverID}
						onOutsideClick={(ev) => hideOnClickOutside(ev, popoverID, uniqueID)}
					>
						<PopoverWrapper
							direction={direction}
							position={position}
							anchorPosition={anchorPosition[`${popoverID}${uniqueID}`]}
							overflow={overflow}
							hasMouseClickAction={hasMouseClickAction}
							zIndex={zIndex ?? popoverList.length + ZIndex.POPUP}
						>
							{content}
						</PopoverWrapper>
					</OutsideClickDetector>
				);
			})}
		</div>,
		document.body
	);
}
